import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMutation, useQuery } from '@apollo/client';

import { Loading } from 'core';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Button } from 'core/components/button';
import Checkbox from 'core/components/checkbox';
import { TextField } from 'core/components/text-field';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { dateTimeFormatter } from 'core/helpers/formatters';

import {
  ADD_INTERNAL_UW_NOTE,
  DELETE_INTERNAL_UW_NOTE,
  EDIT_INTERNAL_UW_NOTE,
  GET_INTERNAL_UW_NOTES,
  SET_INTERNAL_UW_NOTE_STICKY
} from './internal-uw-notes.queries';
import {
  canEditInternalUwNotes,
  canViewInternalUwNotes,
  hasRecord,
  isErrorUnion,
  notesFromResult,
  partitionNotes,
  recordVars,
  unionErrorMessage
} from './internal-uw-notes.helpers';
import useStyles from './internal-uw-notes.styles';

/**
 * Staff-only internal underwriting notes (#35208). One shared panel used across the Quote,
 * Application (both the offer) and Policy screens. Pass `offerId` (pre-bind) or `policyId`
 * (post-bind) — the resolver maps a policyId to its stem so notes travel across renewals.
 *
 * Layout (#35586): the panel is split into two stacked zones. A **Pinned** zone at the top holds
 * the sticky notes (newest-first); the zone is omitted entirely when nothing is sticky. Below it
 * the **active** zone holds the add-note box at the top — always reachable without scrolling past a
 * long history — followed by the non-sticky notes (newest-first). Keeping pinned and non-pinned in
 * physically separate groups makes the pin obvious and removes the old confusion where toggling
 * Sticky re-sorted a single flat list and looked like a different row's checkbox had changed.
 *
 * Never render this for agents: the component refuses to render (and skips the query) unless the
 * session says the user is a non-agency staff user (`canViewInternalUwNotes`). Any internal staff
 * member qualifies — no permission group is required, so carrier staff see it too. ViewOnly
 * staff can read notes but get no add/edit/delete/sticky controls (`canEditInternalUwNotes`),
 * mirroring the schema, which keeps ViewOnly out of the mutations.
 *
 * The panel sits on a white Card, so every TextField/Checkbox inside it uses `mode="light"`; the
 * default styling is light-on-dark and made typed text invisible (#35208 reopen). Each Checkbox
 * gets a unique `id` (the core Checkbox requires one) so no two note rows ever share a control.
 *
 * The backend returns business failures as a normal `InternalUwNotesError` union member (not a
 * thrown GraphQL error), so Apollo's onError never fires for them — every call inspects the union
 * via `isErrorUnion` before treating it as success.
 */
const InternalUwNotes = ({ offerId, policyId }) => {
  const classes = useStyles();
  const toast = useToast();
  const session = useSession();

  const vars = recordVars({ offerId, policyId });
  const skip = !hasRecord(vars) || !canViewInternalUwNotes(session);
  const canEdit = canEditInternalUwNotes(session);

  const { data, loading, refetch } = useQuery(GET_INTERNAL_UW_NOTES, {
    variables: vars,
    skip,
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      if (isErrorUnion(result?.getInternalUwNotes)) {
        toast.notify({
          type: 'error',
          message: unionErrorMessage(result?.getInternalUwNotes, 'Failed to load internal notes')
        });
      }
    }
  });

  const [addNote, { loading: adding }] = useMutation(ADD_INTERNAL_UW_NOTE);
  const [editNote] = useMutation(EDIT_INTERNAL_UW_NOTE);
  const [setSticky] = useMutation(SET_INTERNAL_UW_NOTE_STICKY);
  const [deleteNote] = useMutation(DELETE_INTERNAL_UW_NOTE);

  const [newText, setNewText] = useState('');
  const [newSticky, setNewSticky] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');

  if (skip) {
    return null;
  }

  const { sticky, internal } = partitionNotes(notesFromResult(data));
  const total = sticky.length + internal.length;

  // Run a mutation and surface both transport errors (throw) and business errors (the
  // InternalUwNotesError union member). Returns true only on a real success, so callers
  // don't discard the user's input on failure.
  const runMutation = async (mutate, field, variables, action) => {
    try {
      const { data: result } = await mutate({ variables });
      if (isErrorUnion(result?.[field])) {
        toast.notify({ type: 'error', message: unionErrorMessage(result?.[field], `Couldn't ${action} the note`) });
        return false;
      }
      await refetch();
      return true;
    } catch {
      toast.notify({ type: 'error', message: `Couldn't ${action} the note` });
      return false;
    }
  };

  const onAdd = async () => {
    const text = newText.trim();
    if (!text) {
      return;
    }
    const ok = await runMutation(addNote, 'addInternalUwNote', { input: { ...vars, text, sticky: newSticky } }, 'add');
    if (ok) {
      setNewText('');
      setNewSticky(false);
    }
  };

  const onSaveEdit = async (noteId) => {
    const text = editingText.trim();
    if (!text) {
      return;
    }
    const ok = await runMutation(editNote, 'editInternalUwNote', { input: { ...vars, noteId, text } }, 'update');
    if (ok) {
      setEditingId(null);
      setEditingText('');
    }
  };

  const onToggleSticky = (note) =>
    runMutation(
      setSticky,
      'setInternalUwNoteSticky',
      { input: { ...vars, noteId: note.noteId, sticky: !note.sticky } },
      'update'
    );

  const onDelete = (noteId) => {
    // Soft delete (row is retained for the back end); confirm since it disappears from the UI.
    if (typeof window !== 'undefined' && !window.confirm('Delete this internal note?')) {
      return;
    }
    runMutation(deleteNote, 'deleteInternalUwNote', { input: { ...vars, noteId } }, 'delete');
  };

  // One note row, shared by both zones. The Sticky checkbox carries a per-note id so every row
  // renders a distinct control and toggling one never touches another (#35586).
  const renderNote = (note) => {
    const isEditing = editingId === note.noteId;
    return (
      <div key={note.noteId} className={classNames(classes.note, { [classes.stickyNote]: note.sticky })}>
        {isEditing ? (
          <TextField
            className={classes.editField}
            mode="light"
            multiline
            minRows={2}
            value={editingText}
            onChange={(event) => setEditingText(event.target.value)}
          />
        ) : (
          <div className={classes.noteText}>{note.text}</div>
        )}
        <div className={classes.noteFooter}>
          <div className={classes.noteMeta}>
            <span className={classes.noteAuthor}>{note.author}</span>
            <span className={classes.noteDate}>
              {dateTimeFormatter(note.createdAt)}
              {note.updatedAt ? ' · edited' : ''}
            </span>
          </div>
          {canEdit && (
            <div className={classes.noteActions}>
              <Checkbox
                id={`uw-note-sticky-${note.noteId}`}
                label="Sticky"
                mode="light"
                value={!!note.sticky}
                onChange={() => onToggleSticky(note)}
              />
              <div className={classes.noteActionButtons}>
                {isEditing ? (
                  <>
                    <Button variant="text" color="secondary" onClick={() => onSaveEdit(note.noteId)}>
                      Save
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        setEditingId(null);
                        setEditingText('');
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={() => {
                        setEditingId(note.noteId);
                        setEditingText(note.text);
                      }}
                    >
                      Edit
                    </Button>
                    <Button variant="text" onClick={() => onDelete(note.noteId)}>
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Section title="Internal UW Notes" rightLabel="Notes" rightValue={loading && !total ? '...' : `${total}`}>
      <Card>
        {loading && !total ? (
          <Loading />
        ) : (
          <>
            {sticky.length > 0 && (
              <div className={classes.pinnedZone} data-testid="uw-notes-pinned">
                <div className={classes.zoneHeading}>Pinned</div>
                {sticky.map(renderNote)}
              </div>
            )}
            <div className={classes.activeZone} data-testid="uw-notes-active">
              {canEdit && (
                <div className={classes.addRow}>
                  <TextField
                    placeholder="Add an internal note…"
                    mode="light"
                    multiline
                    minRows={2}
                    value={newText}
                    onChange={(event) => setNewText(event.target.value)}
                  />
                  <div className={classes.addControls}>
                    <Checkbox
                      id="uw-note-new-sticky"
                      label="Sticky (pin to top)"
                      mode="light"
                      value={newSticky}
                      onChange={(event) => setNewSticky(event.target.checked)}
                    />
                    <Button variant="contained" color="primary" disabled={adding || !newText.trim()} onClick={onAdd}>
                      Add note
                    </Button>
                  </div>
                </div>
              )}
              {total === 0 && (
                <div className={classes.empty}>No internal notes yet. Staff-only — never shown to agents.</div>
              )}
              {internal.length > 0 && <div className={classes.notesList}>{internal.map(renderNote)}</div>}
            </div>
          </>
        )}
      </Card>
    </Section>
  );
};

InternalUwNotes.propTypes = {
  offerId: PropTypes.string,
  policyId: PropTypes.string
};

InternalUwNotes.defaultProps = {
  offerId: undefined,
  policyId: undefined
};

export default InternalUwNotes;
